import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { X, Calendar as CalendarIcon, Clock, AlertCircle, CheckCircle, Loader2, UserX } from 'lucide-react'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import { reviewService } from '@/services/api/reviewService'
import { useAuth } from '@/providers/AuthProvider'

interface ScheduleReviewDialogProps {
  open: boolean
  onClose: () => void
  memberName: string
  memberId: string
  memberEmail?: string
  reviewType?: string
  existingScheduledDate?: string | null
  calendarEventId?: string | null
  onSchedule: (scheduleData: ScheduleData) => void
}

interface ScheduleData {
  reviewType: string
  scheduledDate: Date
  scheduledTime: string
  durationMinutes: number
  location: string
  notes: string
  preparationRequired: boolean
}

export default function ScheduleReviewDialog({
  open,
  onClose,
  memberName,
  memberId,
  memberEmail,
  reviewType: initialReviewType,
  existingScheduledDate,
  calendarEventId,
  onSchedule
}: ScheduleReviewDialogProps) {
  const { user } = useAuth()
  
  // Debug logging
  useEffect(() => {
    if (open) {
      if (import.meta.env.DEV) console.log('📅 ScheduleReviewDialog opened with props:', {
        memberName,
        memberId,
        memberEmail,
        userEmail: user?.email,
        reviewType: initialReviewType,
        existingScheduledDate,
        calendarEventId
      })
    }
  }, [open, memberName, memberId, memberEmail, user?.email, initialReviewType, existingScheduledDate, calendarEventId])
  
  const isRescheduling = !!existingScheduledDate
  const isReviewTypeFixed = !!initialReviewType // Review type is fixed if already set
  const [reviewType, setReviewType] = useState(initialReviewType || '')
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(() => {
    if (existingScheduledDate) {
      // Backend sends UTC time without 'Z', need to add it for correct parsing
      const dateString = existingScheduledDate.endsWith('Z') ? existingScheduledDate : existingScheduledDate + 'Z'
      return new Date(dateString)
    }
    return undefined
  })
  const [scheduledTime, setScheduledTime] = useState(() => {
    if (existingScheduledDate) {
      // Backend sends UTC time without 'Z', need to add it for correct parsing
      const dateString = existingScheduledDate.endsWith('Z') ? existingScheduledDate : existingScheduledDate + 'Z'
      const date = new Date(dateString)
      const hours = date.getHours()
      const minutes = date.getMinutes()
      // Round to nearest 30 minutes for dropdown compatibility
      const roundedMinutes = minutes < 15 ? '00' : minutes < 45 ? '30' : '00'
      const roundedHours = minutes >= 45 ? (hours + 1) % 24 : hours
      return `${String(roundedHours).padStart(2, '0')}:${roundedMinutes}`
    }
    return ''
  })
  const [durationMinutes, setDurationMinutes] = useState(() => {
    // Default durations based on review type
    const durationMap: { [key: string]: number } = {
      'year-end': 180,
      'mid-year': 120,
      'q1-checkin': 60,
      'q3-checkin': 60,
      'jit': 30,
      'probation': 45,
      'adhoc': 30
    }
    return durationMap[initialReviewType || ''] || 60
  })
  const [location, setLocation] = useState('virtual')
  const [notes, setNotes] = useState('')
  
  // Availability checking state
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [availabilityStatus, setAvailabilityStatus] = useState<'available' | 'conflict' | 'unknown'>('unknown')
  const [conflicts, setConflicts] = useState<Array<{ email: string; employee_id?: string; employee_ids?: string[]; busy_items: any[] }>>([])

  // Update date and time when existingScheduledDate changes (e.g., when dialog reopens with different review)
  useEffect(() => {
    if (existingScheduledDate) {
      // Backend sends UTC time without 'Z', need to add it for correct parsing
      const dateString = existingScheduledDate.endsWith('Z') ? existingScheduledDate : existingScheduledDate + 'Z'
      const date = new Date(dateString)
      setScheduledDate(date)
      
      const hours = date.getHours()
      const minutes = date.getMinutes()
      // Round to nearest 30 minutes for dropdown compatibility
      const roundedMinutes = minutes < 15 ? '00' : minutes < 45 ? '30' : '00'
      const roundedHours = minutes >= 45 ? (hours + 1) % 24 : hours
      setScheduledTime(`${String(roundedHours).padStart(2, '0')}:${roundedMinutes}`)
    } else {
      setScheduledDate(undefined)
      setScheduledTime('')
    }
  }, [existingScheduledDate])
  
  // Check availability when date, time, or duration changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (import.meta.env.DEV) console.log('🔍 Availability check conditions:', {
        hasDate: !!scheduledDate,
        hasTime: !!scheduledTime,
        userEmployeeId: user?.employeeId,
        memberId: memberId
      })
      
      if (!scheduledDate || !scheduledTime || !user?.employeeId || !memberId) {
        if (import.meta.env.DEV) console.log('⚠️ Availability check skipped - missing required data')
        setAvailabilityStatus('unknown')
        return
      }
      
      try {
        setIsCheckingAvailability(true)
        
        // Combine date and time
        const [hours, minutes] = scheduledTime.split(':').map(Number)
        const dateTime = new Date(scheduledDate)
        dateTime.setHours(hours, minutes, 0, 0)
        
        // Check availability for both manager and employee using employee IDs
        // Backend will look up primary_contact_email for each user
        // Pass existing calendar event ID to exclude it from conflicts (when rescheduling)
        const result = await reviewService.checkAvailability(
          [user.employeeId, memberId],
          dateTime,
          durationMinutes,
          calendarEventId
        )
        
        if (result.error) {
          console.warn('Availability check failed:', result.error)
          setAvailabilityStatus('unknown')
        } else if (result.available) {
          setAvailabilityStatus('available')
          setConflicts([])
        } else {
          setAvailabilityStatus('conflict')
          setConflicts(result.conflicts || [])
        }
      } catch (error) {
        console.error('Error checking availability:', error)
        setAvailabilityStatus('unknown')
      } finally {
        setIsCheckingAvailability(false)
      }
    }
    
    // Debounce the availability check
    const timeoutId = setTimeout(checkAvailability, 500)
    return () => clearTimeout(timeoutId)
  }, [scheduledDate, scheduledTime, durationMinutes, user?.employeeId, memberId])

  const handleSchedule = () => {
    if (!reviewType || !scheduledDate || !scheduledTime) {
      toast.error('Please complete all required fields', {
        duration: 3000
      })
      return
    }

    onSchedule({
      reviewType,
      scheduledDate,
      scheduledTime,
      durationMinutes,
      location,
      notes,
      preparationRequired: reviewType === 'year-end' || reviewType === 'mid-year'
    })
    onClose()
  }

  const getReviewTypeInfo = (type: string) => {
    const info = {
      'year-end': {
        label: 'Year-End 1:1',
        description: 'Comprehensive formal 1:1 check-in with full KPA coverage and final indicators',
        evidenceRequired: true,
        color: 'bg-purple-100 text-purple-700 border-purple-200'
      },
      'mid-year': {
        label: 'Mid-Year 1:1',
        description: 'Formal 1:1 check-in with separate impact portfolio pool and development focus',
        evidenceRequired: true,
        color: 'bg-blue-100 text-blue-700 border-blue-200'
      },
      'q1-checkin': {
        label: 'Q1 Check-in',
        description: 'Progress check-in focusing on priority KPAs and support needs',
        evidenceRequired: false,
        color: 'bg-green-100 text-green-700 border-green-200'
      },
      'q3-checkin': {
        label: 'Q3 Check-in',
        description: 'Development-focused check-in with selected KPA discussions',
        evidenceRequired: false,
        color: 'bg-amber-100 text-amber-700 border-amber-200'
      },
      'jit': {
        label: 'On-Demand Check-in',
        description: 'Targeted issue or milestone-focused discussion',
        evidenceRequired: false,
        color: 'bg-orange-100 text-orange-700 border-orange-200'
      }
    }
    return info[type as keyof typeof info]
  }

  if (!open) return null

  const selectedTypeInfo = reviewType ? getReviewTypeInfo(reviewType) : null

  const dialogContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[300] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-heritage-blue to-future-blue text-white p-6 sticky top-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <CalendarIcon className="h-6 w-6" />
                {isRescheduling ? 'Reschedule Check-in' : 'Schedule 1:1 check-in dialogue'}
              </h2>
              <p className="text-blue-100 text-sm mt-1">{memberName}</p>
            </div>
            <Button variant="ghost" className="text-white hover:bg-white/20" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Review Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="reviewType" className="text-base font-semibold">
              Check-in Type <span className="text-red-500">*</span>
            </Label>
            {isReviewTypeFixed ? (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-medium text-gray-900">
                  {reviewType === 'year-end' && 'Year-End 1:1 (December)'}
                  {reviewType === 'mid-year' && 'Mid-Year 1:1 (June)'}
                  {reviewType === 'q1-checkin' && 'Q1 Check-in (March)'}
                  {reviewType === 'q3-checkin' && 'Q3 Check-in (September)'}
                  {reviewType === 'jit' && 'On-Demand Check-in (As Needed)'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {isRescheduling ? 'Check-in type cannot be changed when rescheduling' : 'Check-in type is set based on the (impact & growth)+ cycle'}
                </p>
              </div>
            ) : (
              <Select value={reviewType} onValueChange={setReviewType}>
                <SelectTrigger id="reviewType">
                  <SelectValue placeholder="Select check-in type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="year-end">Year-End 1:1 (December)</SelectItem>
                  <SelectItem value="mid-year">Mid-Year 1:1 (June)</SelectItem>
                  <SelectItem value="q1-checkin">Q1 Check-in (March)</SelectItem>
                  <SelectItem value="q3-checkin">Q3 Check-in (September)</SelectItem>
                  <SelectItem value="jit">On-Demand Check-in (As Needed)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Review Type Info Card */}
          {selectedTypeInfo && (
            <div className={`rounded-lg p-4 border ${selectedTypeInfo.color}`}>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{selectedTypeInfo.label}</h3>
                  <p className="text-sm mb-2">{selectedTypeInfo.description}</p>
                  {selectedTypeInfo.evidenceRequired && (
                    <Badge variant="outline" className="text-xs">
                      Impact Portfolio Required
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Date Selection */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">
              Scheduled Date <span className="text-red-500">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {scheduledDate ? format(scheduledDate, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={scheduledDate}
                  onSelect={setScheduledDate}
                  disabled={(date: Date) => {
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    return date < today
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Selection */}
          <div className="space-y-2">
            <Label htmlFor="time" className="text-base font-semibold">
              Scheduled Time <span className="text-red-500">*</span>
            </Label>
            <Select value={scheduledTime} onValueChange={setScheduledTime}>
              <SelectTrigger id="time">
                <SelectValue placeholder="Select time..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="08:00">08:00 AM</SelectItem>
                <SelectItem value="08:30">08:30 AM</SelectItem>
                <SelectItem value="09:00">09:00 AM</SelectItem>
                <SelectItem value="09:30">09:30 AM</SelectItem>
                <SelectItem value="10:00">10:00 AM</SelectItem>
                <SelectItem value="10:30">10:30 AM</SelectItem>
                <SelectItem value="11:00">11:00 AM</SelectItem>
                <SelectItem value="11:30">11:30 AM</SelectItem>
                <SelectItem value="12:00">12:00 PM</SelectItem>
                <SelectItem value="12:30">12:30 PM</SelectItem>
                <SelectItem value="13:00">01:00 PM</SelectItem>
                <SelectItem value="13:30">01:30 PM</SelectItem>
                <SelectItem value="14:00">02:00 PM</SelectItem>
                <SelectItem value="14:30">02:30 PM</SelectItem>
                <SelectItem value="15:00">03:00 PM</SelectItem>
                <SelectItem value="15:30">03:30 PM</SelectItem>
                <SelectItem value="16:00">04:00 PM</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Duration Selection */}
          <div className="space-y-2">
            <Label htmlFor="duration" className="text-base font-semibold">
              Duration
            </Label>
            <Select value={durationMinutes.toString()} onValueChange={(val) => setDurationMinutes(parseInt(val))}>
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1 hour 30 minutes</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="150">2 hours 30 minutes</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Availability Status */}
          {(scheduledDate && scheduledTime) && (
            <div className="mt-4 p-4 rounded-lg border bg-gray-50">
              <div className="flex items-start gap-3">
                {isCheckingAvailability ? (
                  <>
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Checking availability...</p>
                      <p className="text-sm text-gray-600">Verifying calendars for conflicts</p>
                    </div>
                  </>
                ) : availabilityStatus === 'available' ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-900">Both calendars are free</p>
                      <p className="text-sm text-green-700">No conflicts detected for this time slot</p>
                    </div>
                  </>
                ) : availabilityStatus === 'conflict' ? (
                  <>
                    <UserX className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-900">Scheduling conflict detected</p>
                      <p className="text-sm text-amber-700 mb-2">One or more attendees have conflicting appointments</p>
                      {conflicts.length > 0 && (
                        <div className="space-y-2 mt-3">
                          {conflicts.map((conflict, idx) => {
                            // Check if this conflict affects both attendees (shared calendar case)
                            const affectsBoth = conflict.employee_ids &&
                              conflict.employee_ids.includes(user?.employeeId ?? '') &&
                              conflict.employee_ids.includes(memberId)
                            
                            if (affectsBoth) {
                              // Shared calendar - show once
                              return (
                                <div key={idx} className="bg-white p-3 rounded border border-amber-200">
                                  <p className="text-sm font-medium text-gray-900 mb-1">
                                    Both attendees (shared calendar)
                                  </p>
                                  {conflict.busy_items && conflict.busy_items.length > 0 && (
                                    <ul className="space-y-1">
                                      {conflict.busy_items.slice(0, 2).map((item: any, itemIdx: number) => (
                                        <li key={itemIdx} className="text-xs text-gray-600 flex items-start gap-1">
                                          <span className="text-amber-600">•</span>
                                          <span>{item.subject || 'Busy'}</span>
                                        </li>
                                      ))}
                                      {conflict.busy_items.length > 2 && (
                                        <li className="text-xs text-gray-500 italic">
                                          +{conflict.busy_items.length - 2} more
                                        </li>
                                      )}
                                    </ul>
                                  )}
                                </div>
                              )
                            }
                            
                            // Individual conflicts - show separately
                            const isManager = conflict.employee_id === user?.employeeId
                            const displayName = isManager ? 'People Leader (You)' : `Team Member (${memberName})`
                            
                            return (
                              <div key={idx} className="bg-white p-3 rounded border border-amber-200">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  {displayName}
                                </p>
                                {conflict.busy_items && conflict.busy_items.length > 0 && (
                                  <ul className="space-y-1">
                                    {conflict.busy_items.slice(0, 2).map((item: any, itemIdx: number) => (
                                      <li key={itemIdx} className="text-xs text-gray-600 flex items-start gap-1">
                                        <span className="text-amber-600">•</span>
                                        <span>{item.subject || 'Busy'}</span>
                                      </li>
                                    ))}
                                    {conflict.busy_items.length > 2 && (
                                      <li className="text-xs text-gray-500 italic">
                                        +{conflict.busy_items.length - 2} more
                                      </li>
                                    )}
                                  </ul>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      <p className="text-xs text-amber-600 mt-2">
                        ⚠️ You can still schedule, but attendees may need to reschedule existing meetings
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-700">Availability check unavailable</p>
                      <p className="text-sm text-gray-600">Calendar service may be offline</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-base font-semibold">
              Location
            </Label>
            <Select value={location} onValueChange={setLocation}>
              <SelectTrigger id="location">
                <SelectValue placeholder="Select location..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="virtual">Virtual (MS Teams)</SelectItem>
                <SelectItem value="manager-office">People Leader's Office</SelectItem>
                <SelectItem value="meeting-room">Meeting Room</SelectItem>
                <SelectItem value="other">Other Location</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-base font-semibold">
              Agenda & Notes
            </Label>
            <Textarea
              id="notes"
              placeholder="Add agenda items, preparation requirements, or additional notes..."
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Preparation Reminder */}
          {selectedTypeInfo?.evidenceRequired && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Preparation Required</p>
                  <p>
                    Both you and {memberName} should prepare comprehensive impact portfolios before this check-in.
                    A reminder will be sent to {memberName} with preparation instructions.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-4 flex justify-end gap-2 sticky bottom-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSchedule}
            className="bg-heritage-blue"
            disabled={!reviewType || !scheduledDate || !scheduledTime}
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            {isRescheduling ? 'Reschedule Check-in' : 'Schedule 1:1 check-in dialogue'}
          </Button>
        </div>
      </div>
    </motion.div>
  )

  return createPortal(dialogContent, document.body)
}
